import { FC, useEffect, useState } from "react";

import { Stack } from "@mui/material";
import { FormType, Locales } from "@simplyk/common";

import { FundDesignationFormContent } from "./FundDesignationFormContent";
import { useFundSelectionForm } from "./hooks/useFundSelectionForm";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardSwitch } from "@/dashboard-design-system/Switch";
import { Edit, Success } from "@/icons/outlined";

interface EditFundDesignationModalProps {
  isOpen: boolean;
  onClose: () => void;
  donationFormId: string;
  locale: Locales;
  onSaved: () => void;
  onFundAdded?: () => void;
}

export const EditFundDesignationModal: FC<EditFundDesignationModalProps> = ({
  isOpen,
  onClose,
  donationFormId,
  locale,
  onSaved,
  onFundAdded,
}) => {
  const { t } = useTranslate();

  const { selectedFundIds, setSelectedFundIds, hasEnoughFundsSelected, formFundsResult, isSaving, handleSave } =
    useFundSelectionForm({
      formId: donationFormId,
      formType: FormType.DonationForm,
      locale,
      onSaved,
    });

  const [isRequiredToggle, setIsRequiredToggle] = useState(false);

  // Sync isRequired toggle with server data
  useEffect(() => {
    if (formFundsResult) {
      setIsRequiredToggle(formFundsResult.isRequired);
    }
  }, [formFundsResult]);

  return (
    <DashboardDialog open={isOpen} onClose={onClose} maxWidth="sm">
      <DashboardDialogBody
        slots={{
          header: <DashboardDialogHeader icon={<Edit />} title={t("dashboard", "funds.editDesignation.title")} />,
          content: (
            <DashboardDialogContent>
              <Stack spacing={2.5}>
                <DashboardSelect
                  label={t("dashboard", "funds.editDesignation.typeOfQuestion")}
                  disabled
                  value="fund-designation"
                  renderValue={() => (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <DashboardIcon size="small">
                        <Success />
                      </DashboardIcon>
                      {t("dashboard", "funds.editDesignation.fundDesignation")}
                    </Stack>
                  )}
                >
                  <DashboardMenuItem value="fund-designation">
                    {t("dashboard", "funds.editDesignation.fundDesignation")}
                  </DashboardMenuItem>
                </DashboardSelect>

                <FundDesignationFormContent
                  locale={locale}
                  selectedFundIds={selectedFundIds}
                  onSelectedFundIdsChange={setSelectedFundIds}
                  onFundAdded={onFundAdded}
                />

                <DashboardSwitch
                  checked={isRequiredToggle}
                  onChange={(_, checked) => setIsRequiredToggle(checked)}
                  label={t("dashboard", "funds.editDesignation.required")}
                />
              </Stack>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton
                onClick={() => handleSave(isRequiredToggle)}
                isLoading={isSaving}
                disabled={!hasEnoughFundsSelected}
              >
                {t("dashboard", "funds.editDesignation.saveChanges")}
              </DashboardButton>
              <DashboardButton onClick={onClose}>{t("dashboard", "common.cancel")}</DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
